import { ref, onMounted, onUnmounted } from 'vue';
import type { ViewportInfo } from '@/models/viewportInfo';
import type { A365ThemeCode } from '@/composables/a365themes';

export type IncomingMessageType = 'host-page-query-params' | 'theme-changed' | 'viewport-changed';

type IncomingMessageAction = 'host-page-query-params' | 'theme-switch' | 'viewport-size';

type MessageHandler = (messageData: any) => void;

const logPrefix = 'BillingMessageListener.';

interface IMessage {
  action: IncomingMessageAction;
  payload: unknown;
}

type HostQueryParamsPayload = Record<string, string> | { queryParamsDict: Record<string, string> };

interface IHostQueryParams extends IMessage {
  action: 'host-page-query-params';
  payload: HostQueryParamsPayload;
}

interface IMessageThemeSwitch extends IMessage {
  action: 'theme-switch';
  payload: {
    theme: A365ThemeCode;
  };
}

interface IMessageViewportSize extends IMessage {
  action: 'viewport-size';
  payload: {
    viewport: {
      width: number;
      height: number;
    };
    iframeRect: DOMRect;
  };
}

const messageListenersMap = ref(new Map<IncomingMessageAction, Set<MessageHandler>>());

export default function useMessageListener(messageType: IncomingMessageType, handler: MessageHandler) {
  const messageAction = toMessageAction(messageType);
  if (messageAction == null) {
    console.warn(`${logPrefix} Unsupported Message Type: ${messageType}`);
    return;
  }
  const listenersSet = getListenersSet(messageAction);

  onMounted(() => {
    const needToAddListener = !anyListenerRegistered();
    listenersSet.add(handler);
    if (needToAddListener) {
      window.addEventListener('message', handleMessageEvent);
    }
  });

  onUnmounted(() => {
    listenersSet.delete(handler);
    if (!anyListenerRegistered()) {
      window.removeEventListener('message', handleMessageEvent);
    }
  });

  function handleMessageEvent(messageEvent: MessageEvent) {
    const message = messageEvent.data as IMessage;
    handleMessageEventByType(message);
  }
}

function toMessageAction(type: IncomingMessageType): IncomingMessageAction | undefined {
  switch (type) {
    case 'host-page-query-params': return 'host-page-query-params';
    case 'theme-changed': return 'theme-switch';
    case 'viewport-changed': return 'viewport-size';
    default: return undefined;
  }
}

function anyListenerRegistered() {
  for (const listenersSet of messageListenersMap.value.values()) {
    if (listenersSet.size > 0)
      return true;
  }
  return false;
}

function getListenersSet(messageAction: IncomingMessageAction) {
  let listenersSet = messageListenersMap.value.get(messageAction);
  if (listenersSet == null) {
    listenersSet = new Set<MessageHandler>();
    messageListenersMap.value.set(messageAction, listenersSet);
  }
  return listenersSet;
}

function handleMessageEventByType<TMessage extends IMessage>(message: TMessage) {
  switch (message.action) {
    case 'host-page-query-params':
      handleMessage(message as IHostQueryParams, m => resolveHostQueryParamsDict(m.payload));
      break;
    case 'theme-switch':
      handleMessage(message as IMessageThemeSwitch, m => m.payload.theme);
      break;
    case 'viewport-size':
      handleMessage(message as IMessageViewportSize, (m): ViewportInfo => ({
        viewport: {
          width: m.payload.viewport.width,
          height: m.payload.viewport.height
        },
        frameRect: {
          top: m.payload.iframeRect.top,
          left: m.payload.iframeRect.left,
          bottom: m.payload.iframeRect.bottom,
          right: m.payload.iframeRect.right
        }
      }));
      break;
  }
}

function hasQueryParamsDict(
  payload: HostQueryParamsPayload,
): payload is { queryParamsDict: Record<string, string> } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'queryParamsDict' in payload &&
    typeof (payload as { queryParamsDict: unknown }).queryParamsDict === 'object'
  );
}

function resolveHostQueryParamsDict(payload: HostQueryParamsPayload): Record<string, string> {
  if (hasQueryParamsDict(payload)) {
    return payload.queryParamsDict;
  }
  if (payload && typeof payload === 'object') {
    return payload;
  }
  console.warn(`${logPrefix} Unexpected host-page-query-params payload shape: ${JSON.stringify(payload)}`);
  return {};
}

function handleMessage<TMessage extends IMessage>(message: TMessage, getPayload: (message: TMessage) => any) {
  console.log(`${logPrefix} Message received. ${JSON.stringify(message)}`);

  const payload = getPayload(message);
  const listenersSet = getListenersSet(message.action);
  listenersSet.forEach(handler => handler(payload));
}
